import { OnboardingBotSetupContext } from "features/onboarding/guided/OnboardingBotSetup.context";
import { createPortal } from "react-dom";
import OnboardingConfirmPanel from "features/onboarding/guided/OnboardingConfirmPanel";
import React, { ReactNode, useContext } from "react";
import { AmountType, BotType, LanguageNameSpaces } from "@shared/types";
import styled, { css, useTheme } from "styled-components";
import {
  formPanel,
  FormSegmented,
  PhoneFormSegmented,
} from "features/bots/BotForm/NewLook/styles";
import { NEW_LOOK_PHONE_HIT } from "common/NewLook/layout";
import {
  AlertCircle,
  CheckCircle2,
  LineChart,
  ListOrdered,
} from "lucide-react";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import SaveBotButton from "features/bots/common/SaveBotButton";
import { DcaBotCreateeditOriginValueType } from "analytics/events";
import CheckOutlined from "@ant-design/icons/CheckOutlined";
import HistoryOutlined from "@ant-design/icons/HistoryOutlined";
import LineChartOutlined from "@ant-design/icons/LineChartOutlined";
import UnorderedListOutlined from "@ant-design/icons/UnorderedListOutlined";
import WarningOutlined from "@ant-design/icons/WarningOutlined";
import {
  paddingStyles,
  WarningWrapper,
} from "features/bots/BotForm/common/styles";
import Button from "common/Buttons/Button";
import BotFormSummary from "features/bots/BotForm/common/BotFormSummary";
import {
  BotFormType,
  HedgingMode,
  Steps,
} from "@shared/features/bots/BotForm/common/types";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import useSummary from "@shared/features/bots/BotForm/OrdersSummary/useSummary";
import BotConfigurationModal from "features/bots/BotForm/common/BotConfigurationModal";
import HighlightedText from "common/Text/HighlightedText";
import OrdersSummaryModal from "features/bots/BotForm/OrdersSummaryModal";
import SummaryDrawer from "features/bots/BotForm/SummaryDrawer";
import Modal from "common/Modal/Modal";
import BotsSummary from "features/bots/BotForm/Summaries/BotsSummary";
import useMedia from "utils/hooks/useMedia";
import Link from "common/Typography/Link";
import DefaultGroupConfigurationMessage from "features/bots/BotForm/common/DefaultGroupConfigurationMessage";
import { isSymbol } from "@shared/types/utils";
import Text from "common/Typography/Text";
import SignalsBotSummaryTabs from "features/bots/BotForm/Summaries/SignalsBotSummaryTabs";
import BacktestModal from "features/bots/BotForm/Backtesting/BacktestModal";
import useQuotaModal from "features/settings/Quota/useQuotaModal";
import Tooltip from "common/Tooltips/Tooltip";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import useShowNoAccountCard from "features/bots/BotForm/common/useShowNoAccountCard";
import useTrackSaveBlocked from "features/bots/common/useTrackSaveBlocked";
import { NoAccountCardHighlightContext } from "features/bots/BotForm/common/NoAccountCardHighlight.context";
import {
  BOT_FORM_ADVANCED_SETTINGS_BUTTON_ID,
  BOT_FORM_REVIEW_BUTTON_ID,
  BOT_FORM_SUMMARY_ERROR_ID,
  ONBOARDING_CONFIRM_CREATE_ID,
} from "testIds.ts";

/** New look: the summary's pieces, placed by the form layout. */
export interface NewLookSummarySlots {
  /** Summary rows and the actions, pinned under the form. */
  footer: ReactNode;
  /** Content for the side column (signals pairs, bulk bots lists). */
  aside?: ReactNode;
  /** Notices shown at the top of the form (group configuration). */
  notice?: ReactNode;
  /** Basic / advanced settings switch (grid and signals bots). */
  advancedSwitch?: ReactNode;
}

interface SummaryProps {
  tabsWithErrors: Set<Steps>;
  disabled: boolean;
  botId?: number;
  isFetching: boolean;
  analyticsBotOriginType: DcaBotCreateeditOriginValueType;
  copiedFrom?: number;
  isMobile: boolean;
  /** New look: renders the form layout with the summary's pieces. */
  renderNewLook?: (slots: NewLookSummarySlots) => ReactNode;
  /** New look on a phone: one compact action row and the switch as a slot. */
  isNewLookPhone?: boolean;
}

const Summary = ({
  tabsWithErrors,
  botId,
  disabled,
  analyticsBotOriginType,
  copiedFrom,
  isMobile,
  renderNewLook,
  isNewLookPhone,
}: SummaryProps) => {
  const onboardingSetup = useContext(OnboardingBotSetupContext);
  const { t: onboardingText } = useTypedTranslation(
    LanguageNameSpaces.ONBOARDING
  );
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const {
    amountValue,
    amountType,
    botType,
    isFetching,
    getBotAmountDataStatus,
    botFormType,
    isChannelConfiguration,
    subGroup,
    account,
    hedgingMode,
    isAdvancedView,
  } = useContext(BotFormContext)!;
  const appTheme = useTheme();
  const isTwoLinesSummary = useMedia([`(max-width: 550px)`], [true], false);
  const {
    errors: formErrors,
    summaryDrawerOpen,
    chartDrawerOpen,
    setSummaryDrawerOpen,
    closeSummaryDrawer,
    setChartDrawerOpen,
    entriesSummaryData,
    takeProfitsSummaryData,
    configurationModalData,
    botTemplateConfigurationWarning,
    onResetToConfigurationClick,
    gridsSummaryModalOpen,
    setGridsSummaryModalOpen,
    advancedButtonText,
    onAdvancedSettingsClick,
    backToBasicsModalData,
    showAdvancedButton,
    showBotConfiguration,
    backtestingButtonData,
    backtestModalData,
    signalsDefaultSummaryTab,
    quotaModal,
    showBacktesting,
  } = useSummary({ tabsWithErrors, isTwoLinesSummary, useQuotaModal });
  const isBulkAction =
    botFormType === BotFormType.BULK_EDIT ||
    botFormType === BotFormType.BULK_CREATE;
  const showNoAccountCard = useShowNoAccountCard();
  const trackSaveBlocked = useTrackSaveBlocked();
  const { highlightCard } = useContext(NoAccountCardHighlightContext);
  const pointToNoAccountCard =
    showNoAccountCard && !isBulkAction && !onboardingSetup;
  const errors = pointToNoAccountCard ? undefined : formErrors;
  const saveHighlightProps = pointToNoAccountCard
    ? {
        showAsEnabled: true,
        blockedReason: NO_ACCOUNT_BLOCKED_REASON,
        onDisabledClick: highlightCard,
      }
    : {};
  const onReviewClick = () => {
    if (pointToNoAccountCard) {
      trackSaveBlocked(NO_ACCOUNT_BLOCKED_REASON);
      highlightCard();
    } else {
      setSummaryDrawerOpen(true);
    }
  };
  const isSignalsBot = botType === BotType.SIGNAL;
  const isGridBot = botType === BotType.GRID;
  const isHedgingBot = botType === BotType.HEDGING;
  const isRangeHedgingBot = isHedgingBot && hedgingMode === HedgingMode.RANGE;
  const isTradingViewSignalsBot = botType === BotType.TRADING_VIEW_SIGNALS;
  const buttonFlex = "1 1 140px";
  // New look (phone and tablet): 44px touch targets in the bottom bar.
  // Desktop: every action in the summary column shares the 40px control height.
  const buttonHeight = appTheme.isNewLook
    ? isMobile
      ? NEW_LOOK_TOUCH_HEIGHT
      : NEW_LOOK_BUTTON_HEIGHT
    : BUTTON_HEIGHT;

  const saveBotButton = (
    <SaveBotButton
      // Confirm mode: the same words as the confirmation's own button.
      actionLabel={
        onboardingSetup?.confirming
          ? onboardingText("guided.start_demo_bot")
          : undefined
      }
      botId={botId}
      disabled={disabled}
      analyticsBotOriginType={analyticsBotOriginType}
      errorMessage={errors}
      {...saveHighlightProps}
      copiedFrom={copiedFrom}
      key="saveBotButton"
      style={{
        height: buttonHeight,
        flex: buttonFlex,
      }}
    />
  );

  const advancedButton = showAdvancedButton ? (
    <Button
      data-testid={BOT_FORM_ADVANCED_SETTINGS_BUTTON_ID}
      $type="secondary"
      onClick={onAdvancedSettingsClick}
      style={{ height: buttonHeight, flex: buttonFlex }}
      key="advancedSettings"
    >
      {advancedButtonText}
    </Button>
  ) : undefined;

  const backtestingButton = backtestingButtonData ? (
    <Button
      $type="secondary"
      onClick={backtestingButtonData.onClick}
      style={{
        height: buttonHeight,
        flex: buttonFlex,
      }}
      loading={backtestingButtonData.isLoading}
      key="backtesting"
    >
      {backtestingButtonData.text}
    </Button>
  ) : undefined;

  const showGridSummaryButton =
    !isBulkAction &&
    !isMobile &&
    !isSignalsBot &&
    (!isHedgingBot || isRangeHedgingBot);

  const getSummaryButtons = () => {
    const buttons = [saveBotButton];
    if (showBacktesting && backtestingButton && !isChannelConfiguration)
      buttons.unshift(backtestingButton);
    // New-look phones switch basic/advanced at the top of the form; the
    // review sheet (this summary) keeps only the decision.
    if (advancedButton && !(appTheme.isNewLook && isMobile))
      buttons.unshift(advancedButton);
    if (showGridSummaryButton) {
      buttons.unshift(
        <Tooltip
          key="summary"
          title={
            isRangeHedgingBot
              ? t("summary.hedging_summary")
              : isGridBot
              ? t("summary.grids_summary")
              : t("summary.orders_summary")
          }
          placement="top"
        >
          <SummaryButton
            $type="secondary"
            onClick={() => setGridsSummaryModalOpen(true)}
            key="advancedSettings"
            style={
              appTheme.isNewLook
                ? { height: buttonHeight, width: buttonHeight }
                : undefined
            }
          >
            <UnorderedListOutlined />
          </SummaryButton>
        </Tooltip>
      );
    }
    return (
      <ButtonsWrapper
        key="buttons"
        style={{
          marginTop:
            isChannelConfiguration || (isTradingViewSignalsBot && !isBulkAction)
              ? 0
              : 16,
        }}
        $wrap={isSignalsBot}
      >
        {buttons}
      </ButtonsWrapper>
    );
  };

  const botSummary = (
    <LoadingSpinnerOverlay
      isLoading={getBotAmountDataStatus.isFetching || isFetching}
      backgroundColor={appTheme.colors.cardBgColor}
      overlayOpacity={0.85}
      wrap={true}
    >
      {!isChannelConfiguration && <BotFormSummary key="botSummary" />}
      {getSummaryButtons()}
    </LoadingSpinnerOverlay>
  );

  const getMobileSummaryText = () => {
    if (pointToNoAccountCard) return;
    if (appTheme.isNewLook) {
      if (errors) {
        return (
          <NewLookStatus
            data-testid={BOT_FORM_SUMMARY_ERROR_ID}
            $isError={true}
          >
            <AlertCircle size={15} strokeWidth={1.75} />
            <span>{errors}</span>
          </NewLookStatus>
        );
      }
      return (
        <NewLookStatus>
          <CheckCircle2 size={15} strokeWidth={1.75} />
          <span>
            {onboardingSetup
              ? onboardingText("revision.review_allocation", {
                  amount: `${
                    typeof amountValue === "symbol" ? "—" : amountValue ?? "—"
                  } ${
                    amountType === AmountType.FIXED_USD_AMOUNT
                      ? "USD"
                      : amountType === AmountType.FIXED_BTC_AMOUNT
                      ? "BTC"
                      : "%"
                  }`,
                })
              : t("summary.bot_ready_confirmation")}
          </span>
        </NewLookStatus>
      );
    }
    if (errors) {
      return (
        <WarningWrapper data-testid={BOT_FORM_SUMMARY_ERROR_ID} type="danger">
          <WarningOutlined />
          {errors}
        </WarningWrapper>
      );
    } else {
      return (
        <WarningWrapper style={{ color: appTheme.colors.primaryBlue }}>
          <CheckOutlined />
          {onboardingSetup
            ? onboardingText("revision.review_allocation", {
                amount: `${
                  typeof amountValue === "symbol" ? "—" : amountValue ?? "—"
                } ${
                  amountType === AmountType.FIXED_USD_AMOUNT
                    ? "USD"
                    : amountType === AmountType.FIXED_BTC_AMOUNT
                    ? "BTC"
                    : "%"
                }`,
              })
            : t("summary.bot_ready_confirmation")}
        </WarningWrapper>
      );
    }
  };

  const getMobileActionButton = () => {
    if (isChannelConfiguration) return saveBotButton;
    return (
      <Button
        data-testid={BOT_FORM_REVIEW_BUTTON_ID}
        type="primary"
        onClick={onReviewClick}
        style={{
          height: buttonHeight,
          flex: isSignalsBot ? "none" : buttonFlex,
          width: isSignalsBot ? "100%" : "auto",
        }}
        key="review"
      >
        {t("buttons.review")}
      </Button>
    );
  };

  const getContent = () => {
    if (isMobile) {
      return (
        <>
          {getMobileSummaryText()}
          <ButtonsWrapper $wrap={isSignalsBot}>
            {!isBulkAction && !isSignalsBot && !isHedgingBot && (
              <SummaryButton
                $type="secondary"
                onClick={() => setChartDrawerOpen(true)}
                key="chartButton"
              >
                {appTheme.isNewLook ? (
                  <LineChart size={18} strokeWidth={1.75} />
                ) : (
                  <LineChartOutlined />
                )}
              </SummaryButton>
            )}
            {advancedButton}
            {showBacktesting && !isChannelConfiguration && backtestingButton}
            {getMobileActionButton()}
          </ButtonsWrapper>
        </>
      );
    } else if (isBulkAction) {
      return (
        <>
          <BorderedSummaryWrapper>{botSummary}</BorderedSummaryWrapper>
          <BotsSummary
            isValid={false}
            onBotClick={showBotConfiguration}
            key="validBots"
          />
          <BotsSummary
            isValid={true}
            onBotClick={showBotConfiguration}
            key="invalidBots"
          />
        </>
      );
    } else if (isSignalsBot) {
      return (
        <>
          <DefaultGroupConfigurationMessage
            style={{ marginBottom: 16 }}
            subGroupId={subGroup?.id}
            exchange={
              account && !isSymbol(account) ? account.exchange : undefined
            }
          />
          <BorderedSummaryWrapper>{botSummary}</BorderedSummaryWrapper>
          {!isChannelConfiguration && (
            <BorderedSummaryWrapper
              style={{ marginTop: 16, padding: "20px 0 0" }}
            >
              <SignalsBotSummaryTabs defaultTab={signalsDefaultSummaryTab} />
            </BorderedSummaryWrapper>
          )}
        </>
      );
    }
    return botSummary;
  };

  const templateConfigurationWarning = botTemplateConfigurationWarning && (
    <HighlightedText
      style={{ marginBottom: 16 }}
      actionButton={
        <Button
          $type="outlined"
          style={{ marginTop: 12, width: "100%" }}
          onClick={onResetToConfigurationClick}
        >
          <HistoryOutlined /> {t("buttons.reset_to_group_configuration")}
        </Button>
      }
    >
      <div>
        {botTemplateConfigurationWarning}
        <Link
          style={{
            fontStyle: "italic",
            textDecoration: "underline",
            marginLeft: 6,
            fontWeight: 600,
          }}
        >
          {t("buttons.learn_more")}
        </Link>
      </div>
    </HighlightedText>
  );

  // Phone (new look): one row of actions — chart, backtesting, then the
  // primary action filling the rest. The status line is only shown when it
  // says something the button does not (errors, the onboarding allocation).
  const getPhoneFooter = () => {
    const showChartButton = !isBulkAction && !isSignalsBot && !isHedgingBot;
    const showBacktestingButton =
      showBacktesting && !!backtestingButtonData && !isChannelConfiguration;
    return (
      <>
        {(errors || onboardingSetup) && getMobileSummaryText()}
        <PhoneActions>
          {showChartButton && (
            <PhoneIconButton
              $type="secondary"
              onClick={() => setChartDrawerOpen(true)}
            >
              <LineChart size={18} strokeWidth={1.75} />
            </PhoneIconButton>
          )}
          {showBacktestingButton && (
            <Button
              $type="secondary"
              onClick={backtestingButtonData.onClick}
              loading={backtestingButtonData.isLoading}
              style={{
                height: NEW_LOOK_PHONE_HIT,
                padding: "0 16px",
                flex: "none",
              }}
            >
              {backtestingButtonData.text}
            </Button>
          )}
          {isChannelConfiguration ? (
            <SaveBotButton
              botId={botId}
              disabled={disabled}
              analyticsBotOriginType={analyticsBotOriginType}
              errorMessage={errors}
              {...saveHighlightProps}
              copiedFrom={copiedFrom}
              style={{ height: NEW_LOOK_PHONE_HIT, flex: "1 1 auto" }}
            />
          ) : (
            <Button
              data-testid={BOT_FORM_REVIEW_BUTTON_ID}
              type="primary"
              onClick={onReviewClick}
              style={{ height: NEW_LOOK_PHONE_HIT, flex: "1 1 auto" }}
            >
              {t("buttons.review")}
            </Button>
          )}
        </PhoneActions>
      </>
    );
  };

  const getNewLookSlots = (): NewLookSummarySlots => {
    const groupConfigurationMessage = isSignalsBot && !isBulkAction && (
      <DefaultGroupConfigurationMessage
        subGroupId={subGroup?.id}
        exchange={account && !isSymbol(account) ? account.exchange : undefined}
      />
    );
    const notice = (
      <>
        {templateConfigurationWarning}
        {groupConfigurationMessage}
      </>
    );
    if (isNewLookPhone) {
      return {
        footer: getPhoneFooter(),
        notice,
        advancedSwitch: showAdvancedButton ? (
          <PhoneFormSegmented
            value={isAdvancedView ? "advanced" : "basic"}
            onChange={() => onAdvancedSettingsClick()}
            // Phones: the short words fit the two halves.
            options={[
              { value: "basic", label: t("buttons.basic_short") },
              { value: "advanced", label: t("buttons.advanced_short") },
            ]}
          />
        ) : undefined,
      };
    }
    const advancedSwitch =
      showAdvancedButton && !isMobile ? (
        <FormSegmented
          value={isAdvancedView ? "advanced" : "basic"}
          onChange={() => onAdvancedSettingsClick()}
          options={[
            { value: "basic", label: t("buttons.basic_settings") },
            { value: "advanced", label: t("buttons.advanced_settings") },
          ]}
        />
      ) : undefined;
    let aside: ReactNode;
    if (isBulkAction && !isMobile) {
      aside = (
        <>
          <BotsSummary
            isValid={false}
            onBotClick={showBotConfiguration}
            key="validBots"
          />
          <BotsSummary
            isValid={true}
            onBotClick={showBotConfiguration}
            key="invalidBots"
          />
        </>
      );
    } else if (isSignalsBot && !isChannelConfiguration) {
      aside = <SignalsBotSummaryTabs defaultTab={signalsDefaultSummaryTab} />;
    }
    if (isMobile) {
      return { footer: getContent(), notice, aside };
    }
    const showBacktestingButton =
      showBacktesting && backtestingButton && !isChannelConfiguration;
    const footer = (
      <LoadingSpinnerOverlay
        isLoading={getBotAmountDataStatus.isFetching || isFetching}
        backgroundColor={appTheme.colors.cardBgColor}
        overlayOpacity={0.85}
        wrap={true}
      >
        {!isChannelConfiguration && <BotFormSummary variant="plain" />}
        <NewLookActions>
          {showGridSummaryButton && (
            <Tooltip
              title={
                isRangeHedgingBot
                  ? t("summary.hedging_summary")
                  : isGridBot
                  ? t("summary.grids_summary")
                  : t("summary.orders_summary")
              }
              placement="top"
            >
              <NewLookIconButton
                $type="secondary"
                onClick={() => setGridsSummaryModalOpen(true)}
              >
                <ListOrdered size={16} strokeWidth={1.75} />
              </NewLookIconButton>
            </Tooltip>
          )}
          {showBacktestingButton && (
            <Button
              $type="secondary"
              onClick={backtestingButtonData.onClick}
              loading={backtestingButtonData.isLoading}
              style={{ height: NEW_LOOK_BUTTON_HEIGHT, padding: "0 18px" }}
            >
              {backtestingButtonData.text}
            </Button>
          )}
          <SaveBotButton
            botId={botId}
            disabled={disabled}
            analyticsBotOriginType={analyticsBotOriginType}
            errorMessage={errors}
            {...saveHighlightProps}
            copiedFrom={copiedFrom}
            style={{ height: NEW_LOOK_BUTTON_HEIGHT, flex: "1 1 auto" }}
          />
        </NewLookActions>
      </LoadingSpinnerOverlay>
    );
    return { footer, notice, aside, advancedSwitch };
  };

  // Onboarding confirm mode: the confirmation renders into the setup page's
  // slot (the form itself is hidden), with its own create button.
  const confirmPanel =
    onboardingSetup?.confirming && onboardingSetup.confirmSlot
      ? createPortal(
          <OnboardingConfirmPanel
            onCustomize={onboardingSetup.onCustomize}
            // The bot's orders (entries, take-profits or grids), as the
            // summary shows them: a sheet on phones, a dialog on desktop.
            onShowConfiguration={() =>
              showGridSummaryButton
                ? setGridsSummaryModalOpen(true)
                : setSummaryDrawerOpen(true)
            }
            createButton={
              <SaveBotButton
                botId={botId}
                disabled={disabled}
                analyticsBotOriginType={analyticsBotOriginType}
                errorMessage={errors}
                {...saveHighlightProps}
                copiedFrom={copiedFrom}
                testId={ONBOARDING_CONFIRM_CREATE_ID}
                actionLabel={onboardingText("guided.start_demo_bot")}
                buttonSize="large"
              />
            }
          />,
          onboardingSetup.confirmSlot
        )
      : null;

  return (
    <>
      {confirmPanel}
      {renderNewLook ? (
        renderNewLook(getNewLookSlots())
      ) : (
        <>
          {templateConfigurationWarning}
          {getContent()}
        </>
      )}
      <SummaryDrawer
        isOpen={summaryDrawerOpen || chartDrawerOpen}
        isChart={chartDrawerOpen}
        entriesSummaryData={entriesSummaryData}
        takeProfitsSummaryData={takeProfitsSummaryData}
        botSummary={botSummary}
        closeDrawer={closeSummaryDrawer}
      />
      {configurationModalData.botData && (
        <BotConfigurationModal
          bot={configurationModalData.botData}
          open={configurationModalData.isOpen}
          closeModal={configurationModalData.closeModal}
          isBulkEdit={isBulkAction}
        />
      )}
      {showGridSummaryButton && (
        <OrdersSummaryModal
          isOpen={gridsSummaryModalOpen}
          setGridsSummaryModalOpen={setGridsSummaryModalOpen}
          entriesSummaryData={entriesSummaryData}
          takeProfitsSummaryData={takeProfitsSummaryData}
        />
      )}
      <Modal
        onCancel={backToBasicsModalData.onCancel}
        open={backToBasicsModalData.isOpen}
        title={backToBasicsModalData.title}
        onOk={backToBasicsModalData.onOk}
        destroyOnHidden={true}
      >
        <Text>{backToBasicsModalData.text}</Text>
      </Modal>
      {showBacktesting && (
        <BacktestModal
          open={backtestModalData.open}
          closeModal={backtestModalData.closeModal}
        />
      )}
      {quotaModal}
    </>
  );
};

export default Summary;

const NO_ACCOUNT_BLOCKED_REASON = "no_account";
const BUTTON_HEIGHT = 38;
const NEW_LOOK_BUTTON_HEIGHT = 40;
const NEW_LOOK_TOUCH_HEIGHT = 44;

const BorderedSummaryWrapper = styled.div`
  background: ${(p) => p.theme.colors.cardBgColor};
  position: relative;
  border: ${(p) => p.theme.borders.medium};
  ${paddingStyles};
  border-radius: 8px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      ${formPanel};
      padding: 16px;
    `};
`;
const ButtonsWrapper = styled.div<{ $wrap?: boolean }>`
  display: flex;
  column-gap: 8px;
  row-gap: 8px;
  margin-top: 16px;
  ${(p) =>
    p.$wrap &&
    `
    flex-wrap: wrap;
  `};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      align-items: center;
      button {
        border-radius: 8px;
      }
    `};
`;
const SummaryButton = styled(Button)`
  height: ${BUTTON_HEIGHT}px;
  padding: 0 4px;
  flex: 0 0 auto;
  width: 40px;
  font-size: 16px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      width: ${NEW_LOOK_TOUCH_HEIGHT}px;
      height: ${NEW_LOOK_TOUCH_HEIGHT}px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    `};
`;
const NewLookStatus = styled.div<{ $isError?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0 0 12px;
  color: ${(p) =>
    p.$isError ? p.theme.colors.red : p.theme.colors.secondaryText};
  font-size: 12.5px;
  line-height: 1.45;
  svg {
    flex: none;
    margin-top: 1px;
    color: ${(p) => (p.$isError ? p.theme.colors.red : p.theme.colors.green)};
  }
`;
const NewLookActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  /* Equal to the footer's bottom padding. */
  margin-top: 16px;
`;
const NewLookIconButton = styled(Button)`
  flex: none;
  width: ${NEW_LOOK_BUTTON_HEIGHT}px;
  height: ${NEW_LOOK_BUTTON_HEIGHT}px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;
/* Phone (new look): the status line sits 8px over the action row. */
const PhoneActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  ${NewLookStatus} + & {
    margin-top: -4px;
  }
  button {
    border-radius: 8px;
  }
`;
const PhoneIconButton = styled(Button)`
  flex: none;
  width: ${NEW_LOOK_PHONE_HIT}px;
  height: ${NEW_LOOK_PHONE_HIT}px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;
